import { TeamOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Col, Row, Spin, Typography } from 'antd'
import { min, sortBy } from 'lodash'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { MosaicComponent, getPointPermission } from '@/components'
import { PAGE_RESOURCE_POINTS_ENUM, Portfolio, Project, ResourceUser, Team } from '@/schemas'

import { AddUser } from './add-user.component'
import { ResourceUserContainer, getSortWeight } from './resource-user.component'

interface ProjectUsersProps {
  isShowTip?: boolean
  source: Pick<Project | Portfolio | Team, 'selfId' | 'type' | 'email' | 'permissions' | 'departmentId'>
  setUsers?: React.Dispatch<React.SetStateAction<ResourceUser[] | undefined>>
}

export const ResourceUsers: React.FC<ProjectUsersProps> = ({ source, setUsers, isShowTip = true }) => {
  const { t } = useTranslation()
  const { selfId, type: resourceType, email, permissions } = source
  const { canCreate, canRead } = getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_成员, permissions)

  const { data, refresh, loading } = useRequest<ResourceUser[]>(
    {
      url: '/api/userRoles/getResourceUsers',
      params: {
        selfId,
        type: resourceType,
      },
    },
    {
      refreshDeps: [selfId, email],
      manual: !canRead,
    }
  )

  const { data: allRoles = [] } = useRequest<any[]>(
    {
      url: '/api/roles/getRoles',
    },
    {
      cacheKey: 'project-user-roles',
      cacheTime: -1,
      staleTime: -1,
      refreshOnWindowFocus: false,
    }
  )

  const roleOptions = allRoles.map((r) => ({ label: r.name, value: r.name }))

  // 埋点中使用，统计 viewer 是否为项目成员
  React.useEffect(() => {
    if (setUsers && data) {
      setUsers(data)
    }
  }, [data])

  const users = data || []

  const avatars = sortBy(users, (u) => min(u.roles.map((r) => getSortWeight(r)))).map((user) => (
    <Col key={user.email} md={8} lg={6} xl={5}>
      <ResourceUserContainer
        selfId={selfId}
        user={user}
        permissions={permissions}
        roleOptions={roleOptions}
        onChange={refresh}
        resourceType={resourceType}
      />
    </Col>
  ))

  const addUser = canCreate && (
    <Col md={8} lg={6} xl={5}>
      <AddUser selfId={selfId} existingEmails={users.map((r) => r.email)} onSuccess={refresh} type={resourceType} />
    </Col>
  )

  return (
    <Spin spinning={loading} delay={300}>
      <div className='flex items-center mb-2'>
        <Typography.Title level={4} className='flex-shrink-0 mr-8px !mb-0'>
          <TeamOutlined className='mr-8px' />
          {t('users')}
        </Typography.Title>
        {isShowTip && (
          <div>
            <Trans
              i18nKey='users description'
              components={{
                Link: <Link className='font-medium' to='?tab=userResource' />,
              }}
            ></Trans>
          </div>
        )}
      </div>
      <Row gutter={[16, 16]} className='pt-4px'>
        {addUser}
        {canRead ? avatars : <MosaicComponent />}
      </Row>
    </Spin>
  )
}
